import { Box, Sheet, Typography } from '@mui/joy';
import { motion } from 'framer-motion';
import AuthScene from './AuthScene';

/**
 * Премиальная «стеклянная» карточка авторизации: фон-аурора, оркестрованное
 * появление, «дышащий» бейдж-иконка (мотив пульса — отсылка к клинике),
 * градиентная волосяная рамка и мягкое свечение.
 * props: icon, title, subtitle, maxWidth=400, footer, children
 */
export default function AuthCard({ icon, title, subtitle, maxWidth = 400, footer, children }) {
  return (
    <Box sx={{
      position: 'relative', minHeight: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2, overflow: 'hidden',
    }}>
      <AuthScene />

      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        sx={{ width: '100%', maxWidth, position: 'relative', zIndex: 1 }}
      >
        {/* мягкое свечение под карточкой */}
        <Box aria-hidden sx={{
          position: 'absolute', inset: -1, borderRadius: 28,
          background: 'radial-gradient(60% 60% at 50% 0%, rgba(123,116,224,0.35) 0%, rgba(123,116,224,0) 70%)',
          filter: 'blur(24px)', zIndex: -1,
        }} />

        <Sheet
          variant="outlined"
          sx={{
            position: 'relative',
            p: { xs: 3, sm: 4 },
            borderRadius: 28,
            border: 'none',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.66) 100%)',
            backdropFilter: 'blur(22px) saturate(160%)',
            WebkitBackdropFilter: 'blur(22px) saturate(160%)',
            boxShadow: '0 30px 70px -28px rgba(46,43,99,0.45), inset 0 1px 0 rgba(255,255,255,0.6)',
            display: 'flex', flexDirection: 'column', gap: 2,
            // градиентная «волосяная» рамка
            '&::before': {
              content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit',
              padding: '1px',
              background: 'linear-gradient(160deg, rgba(255,255,255,0.9), rgba(123,116,224,0.35) 45%, rgba(143,184,245,0.25) 70%, rgba(255,255,255,0.15))',
              WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor', maskComposite: 'exclude',
              pointerEvents: 'none',
            },
          }}
        >
          {/* Заголовок + «дышащий» бейдж */}
          <Box sx={{ textAlign: 'center', mb: 0.5 }}>
            {icon && (
              <Box sx={{
                position: 'relative', width: 76, height: 76, mx: 'auto', mb: 1.75,
                display: 'grid', placeItems: 'center',
                '@keyframes pulse': {
                  '0%':   { transform: 'translate(-50%,-50%) scale(0.9)', opacity: 0.5 },
                  '70%':  { transform: 'translate(-50%,-50%) scale(1.7)', opacity: 0 },
                  '100%': { transform: 'translate(-50%,-50%) scale(1.7)', opacity: 0 },
                },
                '& .halo': {
                  position: 'absolute', top: '50%', left: '50%',
                  width: 56, height: 56, borderRadius: '50%',
                  border: '2px solid rgba(123,116,224,0.5)',
                  willChange: 'transform, opacity',
                },
                '@media (prefers-reduced-motion: reduce)': {
                  '& .halo': { animation: 'none !important', opacity: 0 },
                },
              }}>
                <Box className="halo" sx={{ animation: 'pulse 2.8s ease-out infinite' }} />
                <Box className="halo" sx={{ animation: 'pulse 2.8s ease-out infinite 1.4s' }} />

                {/* сам бейдж */}
                <Box sx={{
                  position: 'relative', width: 56, height: 56, borderRadius: '50%',
                  display: 'grid', placeItems: 'center', color: '#fff',
                  background: 'linear-gradient(135deg, #7B74E0 0%, #403D84 100%)',
                  boxShadow: '0 12px 26px -10px rgba(64,61,132,0.75), inset 0 1px 0 rgba(255,255,255,0.45)',
                }}>
                  {icon}
                </Box>
              </Box>
            )}

            <Typography level="h2" sx={{ fontWeight: 'xl', letterSpacing: '-0.02em', color: '#2E2B63' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography level="body-sm" sx={{ mt: 0.5 }} textColor="text.tertiary">
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Тело формы + мягкая брендовая стилизация полей и главной кнопки */}
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 2,
            '--Input-focusedHighlight': '#6E67C8',
            '& [class*="Input-root"]': { borderRadius: '12px' },
            '& [class*="variantSolid"]': {
              borderRadius: '12px',
              backgroundImage: 'linear-gradient(135deg, #5B54B8 0%, #403D84 100%)',
              boxShadow: '0 12px 26px -12px rgba(64,61,132,0.75)',
              transition: 'transform .15s ease, box-shadow .25s ease, filter .2s ease',
              '&:hover': { filter: 'brightness(1.07)', transform: 'translateY(-1px)', boxShadow: '0 16px 32px -12px rgba(64,61,132,0.8)' },
              '&:active': { transform: 'translateY(0)' },
            },
          }}>
            {children}
          </Box>

          {footer && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
              {footer}
            </Box>
          )}
        </Sheet>
      </Box>
    </Box>
  );
}